import { useEffect, useRef, useState } from "react";
import { sleep } from "../../../helpers";
import { useForm } from "../../../hooks";
import {
  useScheduleEventStore,
  useScheduleStore,
  usePatientSurgeryStore,
  useFormEventStore,
  useFormMessages,
  useViewMediaStore,
  useTPatientSurgeryProcedureRules,
} from "../../hooks";
import { ErrorMessage } from "../../ui";
import { ProcedureEventsCard } from "../cards";
import { ProcedureFilterForm } from "../forms";
import {
  ConfirmProcedureModal,
  ViewMediaModal,
  CancelProcedureModal,
} from "../modals";
import { Schedule } from "../schedules";
import { ProcedureScheduledToast } from "../utils";

const initialFormData = {};

const formValidations = {};

export const ScheduleProcedureAccordion = () => {
  // Datos del hook ----------------------------------------//
  const {
    // Props
    patientSurgery,

    // Methods
    startScheduleProcedure,
    startCancelProcedure,
  } = usePatientSurgeryStore();

  const {
    // Props
    scheduleEvents,
    scheduleEvent,

    // Methods
    handleSetScheduleEvent,
    handleClearScheduleEvent,
  } = useScheduleEventStore();

  const {
    // Props
    schedule,
  } = useScheduleStore();

  const {
    // Props
    isLoading,
    message,
  } = useFormEventStore();

  const {
    // Props
    hasSeenProcedureVideo,
    hasSeenProcedurePdf,

    // Methods
    startResetMediaSeen,
  } = useViewMediaStore();

  const {
    // Props
    showForm,
    type,
  } = useTPatientSurgeryProcedureRules();

  const { procedure } = patientSurgery.data;

  // States ------------------------------------------------//
  const [formData, setFormData] = useState(initialFormData);
  const [isConfirmProcedureModal, setIsConfirmProcedureModal] = useState(false);
  const [isViewMediaModal, setIsViewMediaModal] = useState(false);
  const [isCancelProcedureModal, setIsCancelProcedureModal] = useState(false);
  const [isProcedureScheduledToast, setIsProcedureScheduledToast] =
    useState(false);
  const errorRef = useRef(null);

  // Formulario --------------------------------------------//
  const { formState, onResetForm } = useForm(formData, formValidations);

  const {
    setFormSubmitted,
    showErrorMessage,
    messageText,
    showSuccessMessage,
  } = useFormMessages({
    message,
    onResetForm,
  });

  const propsToModal = {
    isLoading,
  };

  // Funciones ---------------------------------------------//
  const handleSelectEvent = (event) => {
    const newEvent = {
      ...event,
      start: event.start.toISOString(),
      end: event.end.toISOString(),
    };

    handleSetScheduleEvent(newEvent);
    setIsConfirmProcedureModal(true);
  };

  const handleSelectEventToReschedule = (event) => {
    handleSetScheduleEvent(event);
    setIsCancelProcedureModal(true);
  };

  const handleScheduleSubmit = () => {
    setFormSubmitted(true);
    startScheduleProcedure(formState);
  };

  const handleScheduleCancel = () => {
    setFormSubmitted(true);
    startCancelProcedure();
  };

  const hideProcedureScheduledToast = async () => {
    await sleep(3000);
    setIsProcedureScheduledToast(false);
  };

  const handleViewMediaModal = () => {
    setFormSubmitted(false);
    startResetMediaSeen();
    setIsViewMediaModal(true);
  };

  const handleScrollToError = async () => {
    await sleep(200);
    errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // Efectos -----------------------------------------------//
  useEffect(() => {
    if (!isConfirmProcedureModal && !isCancelProcedureModal && scheduleEvent) {
      handleClearScheduleEvent();
    }
  }, [isConfirmProcedureModal, scheduleEvent, isCancelProcedureModal]);

  useEffect(() => {
    setFormData(() => {
      const updated = {
        ...formState,
        type,
      };

      return updated;
    });
  }, [type]);

  useEffect(() => {
    if (showSuccessMessage) {
      if (isConfirmProcedureModal) {
        setIsConfirmProcedureModal(false);
      }

      if (isCancelProcedureModal) {
        setIsCancelProcedureModal(false);
      }

      if (
        !hasSeenProcedureVideo &&
        !hasSeenProcedurePdf &&
        isConfirmProcedureModal
      ) {
        setIsViewMediaModal(true);
      }
    }
  }, [
    showSuccessMessage,
    hasSeenProcedureVideo,
    hasSeenProcedurePdf,
    setIsViewMediaModal,
  ]);

  useEffect(() => {
    if (showErrorMessage) {
      if (isConfirmProcedureModal) {
        setIsConfirmProcedureModal(false);
      }
      if (isCancelProcedureModal) {
        setIsCancelProcedureModal(false);
      }

      if (errorRef.current) {
        handleScrollToError();
      }
    }
  }, [showErrorMessage]);

  useEffect(() => {
    if (messageText === "scheduled procedure successfully") {
      if (!isViewMediaModal && hasSeenProcedureVideo && hasSeenProcedurePdf) {
        setIsProcedureScheduledToast(true);
      }
    }
  }, [
    messageText,
    isViewMediaModal,
    hasSeenProcedureVideo,
    hasSeenProcedurePdf,
  ]);

  useEffect(() => {
    if (isProcedureScheduledToast) {
      hideProcedureScheduledToast();
    }
  }, [isProcedureScheduledToast]);

  // useEffect(() => {
  //   console.log("formState", formState);
  // }, [formState]);

  return (
    <>
      <div className="accordion" id="scheduleProcedureAccordion">
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button
              className={`accordion-button`}
              type="button"
              data-bs-toggle="collapse"
              data-bs-target={`#scheduleProcedure`}
              aria-expanded="true"
              aria-controls={`scheduleProcedure`}
            >
              Procedimiento: {procedure.procedure.title || "Sin título"}
            </button>
          </h2>
          <div
            id={`scheduleProcedure`}
            className={`accordion-collapse collapse show`}
            data-bs-parent="#scheduleProcedureAccordion"
          >
            <div className="accordion-body">
              <div className="vstack gap-4">
                {showErrorMessage && (
                  <ErrorMessage ref={errorRef}>{messageText}</ErrorMessage>
                )}

                {scheduleEvents.length > 0 && (
                  <ProcedureEventsCard
                    handleSelectEventToReschedule={
                      handleSelectEventToReschedule
                    }
                    handleViewMediaModal={handleViewMediaModal}
                  />
                )}

                {showForm && (
                  <>
                    <ProcedureFilterForm />
                    {schedule && (
                      <Schedule handleSelectEvent={handleSelectEvent} />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmProcedureModal
        isOpen={isConfirmProcedureModal}
        setIsOpen={setIsConfirmProcedureModal}
        onSubmit={handleScheduleSubmit}
        propsForm={propsToModal}
      />

      <ViewMediaModal
        isOpen={isViewMediaModal}
        setIsOpen={setIsViewMediaModal}
      />

      <CancelProcedureModal
        isOpen={isCancelProcedureModal}
        setIsOpen={setIsCancelProcedureModal}
        onSubmit={handleScheduleCancel}
        propsForm={propsToModal}
      />

      {isProcedureScheduledToast && <ProcedureScheduledToast />}
    </>
  );
};
